import React from "react";
import {useUser} from "@/context/AuthContext";
import {getUserProfile} from "@/lib/UserProfile";
import {defaultLocale, Locale} from "./locales";
import {getLocalStorageItem, setLocalStorageItem} from "@/lib/storageUtils";
import {
    LEGACY_LOCALE_STORAGE_KEY,
    normalizeMarketingLocale,
    parseLocaleFromSearchParams,
    persistUserLocale,
    resolveVisitorLocale,
    USER_LOCALE_STORAGE_KEY,
} from "./resolveLocale";

const GEO_LOCALE_CACHE_KEY = "braiv:geoLocale";

/** Shared locale resolution for the auth shell and the full app shell. */
export function useResolvedLocale(): Locale {
    const user = useUser();
    const [locale, setLocale] = React.useState<Locale>(defaultLocale);
    const [isClient, setIsClient] = React.useState(false);

    React.useLayoutEffect(() => {
        if (typeof window === "undefined") return;
        setIsClient(true);
        setLocale(resolveVisitorLocale());
    }, []);

    React.useEffect(() => {
        if (!isClient || typeof window === "undefined") return;

        let cancelled = false;

        async function resolveLocale() {
            if (user?.uid) {
                try {
                    const profile = await getUserProfile(user.uid);
                    const pref = normalizeMarketingLocale(profile?.preferredLocale);
                    if (!cancelled && pref) {
                        persistUserLocale(pref);
                        setLocale(pref);
                        return;
                    }
                } catch (error) {
                    console.error("Error loading user locale preference:", error);
                }
            }

            const urlLocale = normalizeMarketingLocale(
                parseLocaleFromSearchParams(new URLSearchParams(window.location.search))
            );
            if (!cancelled && urlLocale) {
                persistUserLocale(urlLocale);
                setLocale(urlLocale);
                return;
            }

            const storedLocale = normalizeMarketingLocale(
                getLocalStorageItem(USER_LOCALE_STORAGE_KEY) ||
                getLocalStorageItem(LEGACY_LOCALE_STORAGE_KEY)
            );
            if (!cancelled && storedLocale) {
                setLocale(storedLocale);
                return;
            }

            const cachedGeoLocale = normalizeMarketingLocale(getLocalStorageItem(GEO_LOCALE_CACHE_KEY));
            if (!cancelled && cachedGeoLocale) {
                setLocale(cachedGeoLocale);
                return;
            }

            try {
                const geoRes = await fetch("/api/geo");
                const {country} = await geoRes.json();
                const geoLocale = country === "BR" ? "pt"
                    : country === "RU" ? "ru"
                    : country === "IT" ? "it"
                    : country === "JP" ? "ja"
                    : null;
                if (geoLocale) {
                    setLocalStorageItem(GEO_LOCALE_CACHE_KEY, geoLocale);
                    if (!cancelled) setLocale(geoLocale as Locale);
                    return;
                }
            } catch {
                // geo detection is best-effort
            }

            if (!cancelled) setLocale(resolveVisitorLocale());
        }

        resolveLocale();
        return () => {
            cancelled = true;
        };
    }, [user?.uid, isClient]);

    return locale;
}
